// Total Working HRS — the ONE derived number of a deployment norm.
//
// CLIENT-SIDE MIRROR ONLY, for the editor's what-if column. The source of
// truth is the backend: prx_project_management_service
// src/utils/machinery-rate.util.ts `computeTotalWorkingHours` (null guards,
// defaults NPF=0 / EUF=1 / AF=1, workbook-verified), then ROUNDED TO WHOLE
// HOURS by the costing layer — the source workbook rounds Total Working HRS
// to integers (42.42 -> 42, 24.66 -> 25) and every amount recalculates from
// the rounded value. Nothing computed here is ever SENT.
//
//   hours = round( (qty × AF) / (prodNorm × EUF) × (1 + NPF) )
//
// Factors are FRACTIONS (0.05 = 5%). A blank factor means "use the default",
// exactly as the backend treats a NULL column. prodNorm 0/blank means the
// equipment is "considered, not deployed" — no hours, not zero hours.

const num = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Hours to produce `qty` output units with one equipment line, or null when
 * the line cannot produce (no/zero prodNorm, or a non-positive EUF — the
 * formula divides by both).
 */
export const computeDeploymentHours = (
  qty: any,
  line: {
    nonProductionHoursFactor?: any;
    effectiveUtilizationFactor?: any;
    applicationFactor?: any;
    prodNorm?: any;
  }
): number | null => {
  const quantity = num(qty);
  const prodNorm = num(line.prodNorm);
  if (quantity === null || !prodNorm || prodNorm <= 0) return null;

  const npf = num(line.nonProductionHoursFactor) ?? 0;
  const euf = num(line.effectiveUtilizationFactor) ?? 1;
  const af = num(line.applicationFactor) ?? 1;
  if (euf <= 0) return null;

  return Math.round(((quantity * af) / (prodNorm * euf)) * (1 + npf));
};

/**
 * Machine-hours per ONE unit of output — the ₹/unit multiplier.
 *
 *   hoursPerUnit = AF × (1 + NPF) / (prodNorm × EUF)
 *
 * CLIENT-SIDE MIRROR of `hoursPerUnitFromFactors` in the backend's
 * machinery-rate.util.ts. It exists for the same reason computeDeploymentHours
 * does: in the norm EDITOR the matrix is being typed, so the server's figures
 * go stale on every keystroke and there is nothing to fetch yet.
 *
 * NOT rounded, unlike computeDeploymentHours — the whole-hour rounding belongs
 * to costing a real quantity, and applying it here would put a ~50% error on a
 * per-unit rate. Same null guards otherwise.
 */
export const computeHoursPerUnit = (line: {
  nonProductionHoursFactor?: any;
  effectiveUtilizationFactor?: any;
  applicationFactor?: any;
  prodNorm?: any;
}): number | null => {
  const prodNorm = num(line.prodNorm);
  if (!prodNorm || prodNorm <= 0) return null;

  const npf = num(line.nonProductionHoursFactor) ?? 0;
  const euf = num(line.effectiveUtilizationFactor) ?? 1;
  const af = num(line.applicationFactor) ?? 1;
  if (euf <= 0) return null;

  return ((1 * af) / (prodNorm * euf)) * (1 + npf);
};

/** Whole-hour display ("42"), dash for null. */
export const formatHours = (hours: number | null): string =>
  hours === null ? "—" : String(hours);
